import { useState, useRef } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Bookmark, CheckCircle, Play, ChevronRight, ThumbsUp } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { EpisodeDetail, WatchProgress, Comment, SeriesEpisode, SharedProps } from '@/types'

interface Props {
  episode: EpisodeDetail
  progress: WatchProgress | null
  is_favorited: boolean
  comments: Comment[]
  series_episodes: SeriesEpisode[] | null
}

const DIFF_COLORS: Record<string, string> = {
  beginner: 'text-green-600',
  intermediate: 'text-orange-500',
  advanced: 'text-red-500',
}

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

function formatSeconds(s: number) {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${String(sec).padStart(2, '0')}`
}

export default function Show({ episode, progress, is_favorited, comments, series_episodes }: Props) {
  const { auth } = usePage<SharedProps>().props
  const videoRef = useRef<HTMLVideoElement>(null)
  const lastSavedPos = useRef<number>(-1)
  const [favorited, setFavorited] = useState(is_favorited)
  const [commentBody, setCommentBody] = useState('')
  const [replyingTo, setReplyingTo] = useState<{ id: number; name: string } | null>(null)
  const [replyBody, setReplyBody] = useState('')
  const [playbackRate, setPlaybackRate] = useState(1)
  const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]

  const cycleRate = () => {
    const next = RATES[(RATES.indexOf(playbackRate) + 1) % RATES.length]
    setPlaybackRate(next)
    if (videoRef.current) videoRef.current.playbackRate = next
  }

  const skip = (secs: number) => {
    if (videoRef.current) videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime + secs)
  }

  const [localComments, setLocalComments] = useState(comments)

  const handleLike = async (commentId: number) => {
    if (!auth.user) return
    const res = await fetch(`/comments/${commentId}/like`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
    })
    if (res.ok) {
      const { likes_count, liked } = await res.json()
      setLocalComments(prev =>
        prev.map(c =>
          c.id === commentId
            ? { ...c, likes_count, liked_by_current_user: liked }
            : c
        )
      )
    }
  }

  const seriesCompleted = series_episodes ? series_episodes.filter(e => e.completed).length : 0
  const seriesTotal = series_episodes?.length ?? 0
  const seriesProgress = seriesTotal > 0 ? Math.round((seriesCompleted / seriesTotal) * 100) : 0

  const handleFavorite = async () => {
    if (!auth.user) { router.visit('/users/sign_in'); return }
    const method = favorited ? 'DELETE' : 'POST'
    const res = await fetch(`/favorites/${episode.slug}`, {
      method,
      headers: { 'X-CSRF-Token': csrfToken() },
    })
    if (res.ok) setFavorited(!favorited)
  }

  const handleMarkComplete = async () => {
    if (!auth.user) return
    await fetch(`/watch_progresses/${episode.slug}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ position: episode.duration_seconds, completed: true }),
    })
    router.reload()
  }

  const handleVideoTimeUpdate = () => {
    if (!auth.user || !videoRef.current) return
    const pos = Math.floor(videoRef.current.currentTime)
    if (pos % 10 === 0 && pos > 0 && pos !== lastSavedPos.current) {
      lastSavedPos.current = pos
      fetch(`/watch_progresses/${episode.slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ position: pos }),
      })
    }
  }

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentBody.trim() || !auth.user) return
    const res = await fetch('/comments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ episode_id: episode.id, body: commentBody }),
    })
    if (res.ok) {
      const comment = await res.json()
      setLocalComments(prev => [...prev, comment])
      setCommentBody('')
    }
  }

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!replyBody.trim() || !auth.user || !replyingTo) return
    const res = await fetch('/comments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ episode_id: episode.id, body: replyBody, parent_id: replyingTo.id }),
    })
    if (res.ok) {
      const reply = await res.json()
      setLocalComments(prev =>
        prev.map(c =>
          c.id === replyingTo.id
            ? { ...c, replies: [...c.replies, reply] }
            : c
        )
      )
      setReplyBody('')
      setReplyingTo(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{episode.title}</h1>
        <p className="text-sm text-gray-400 mb-6">
          <span className={cn('capitalize font-medium', DIFF_COLORS[episode.difficulty])}>{episode.difficulty}</span>
          <span> • {episode.duration} • </span>
          {episode.tags.map(t => <span key={t.slug}>{t.name} • </span>)}
          <span className={episode.is_free ? 'text-green-600' : 'text-brand'}>
            {episode.is_free ? 'Free episode' : 'Pro episode'}
          </span>
        </p>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left */}
          <div className="lg:col-span-2 space-y-5">
            {/* Video */}
            <div className="bg-rose-50 rounded-xl overflow-hidden border border-rose-100">
              {episode.video_url ? (
                <video
                  ref={videoRef}
                  src={episode.video_url}
                  controls
                  className="w-full aspect-video bg-black"
                  onLoadedMetadata={() => {
                    if (progress?.position && videoRef.current) {
                      videoRef.current.currentTime = progress.position
                    }
                  }}
                  onTimeUpdate={handleVideoTimeUpdate}
                />
              ) : (
                <div className="aspect-video flex items-center justify-center bg-rose-100/50">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow">
                    <Play className="w-6 h-6 text-gray-800 fill-gray-800 ml-1" />
                  </div>
                </div>
              )}
              <div className="px-4 py-3 flex items-center gap-5 text-xs font-mono text-gray-500 border-t border-rose-100">
                {progress && <span>{formatSeconds(progress.position)} / {episode.duration}</span>}
                <button onClick={cycleRate} className="hover:text-gray-800 transition-colors font-semibold">{playbackRate}x</button>
                <button onClick={() => skip(-10)} className="hover:text-gray-800 transition-colors" title="Back 10s">⏪10</button>
                <button onClick={() => skip(10)} className="hover:text-gray-800 transition-colors" title="Forward 10s">10⏩</button>
              </div>
            </div>

            {/* Show Notes */}
            {episode.body && (
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Show Notes</h2>
                <div
                  className="prose prose-sm max-w-none text-gray-600 prose-headings:text-gray-900 prose-a:text-brand prose-code:text-brand prose-code:bg-rose-50 prose-code:rounded prose-pre:bg-gray-900"
                  dangerouslySetInnerHTML={{ __html: episode.body }}
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant={progress?.completed ? 'secondary' : 'primary'}
                onClick={handleMarkComplete}
                className="gap-1.5"
              >
                {progress?.completed && <CheckCircle className="w-4 h-4" />}
                {progress?.completed ? 'Completed' : 'Mark complete'}
              </Button>
              <Button variant="secondary" onClick={handleFavorite} className="gap-1.5">
                <Bookmark className={cn('w-4 h-4', favorited && 'fill-current')} />
                {favorited ? 'Saved' : 'Bookmark'}
              </Button>
              <Button variant="secondary" onClick={() => window.open(`mailto:support@getcasts.dev?subject=Feedback: ${encodeURIComponent(episode.title)}`, '_blank')}>Helpful / Improve</Button>
            </div>
          </div>

          {/* Right */}
          <div className="space-y-5">
            {/* Series */}
            {series_episodes && episode.series && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-1">Series: {episode.series.title}</h3>
                <p className="text-xs text-gray-400 mb-3">
                  {seriesCompleted} of {seriesTotal} complete • {seriesProgress}%
                </p>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4">
                  <div className="bg-gray-900 h-1.5 rounded-full" style={{ width: `${seriesProgress}%` }} />
                </div>
                <ul className="space-y-1.5">
                  {series_episodes.map(ep => (
                    <li key={ep.id} className={cn('flex items-center gap-2 text-sm', ep.is_current ? 'font-medium text-gray-900' : 'text-gray-500')}>
                      {ep.completed ? (
                        <CheckCircle className="w-3.5 h-3.5 text-brand shrink-0" />
                      ) : ep.is_current ? (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-900 shrink-0" />
                      ) : (
                        <span className="w-3.5 h-3.5 shrink-0" />
                      )}
                      <Link href={`/episodes/${ep.slug}`} className="hover:text-brand hover:underline">
                        {String(ep.position ?? '').padStart(2, '0')} {ep.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Discussion */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Discussion</h3>
              <div className="space-y-4 mb-4 max-h-72 overflow-y-auto">
                {localComments.map(c => (
                  <div key={c.id} className="text-sm">
                    <p className="font-semibold text-brand mb-0.5">{c.user.name}</p>
                    <p className="text-gray-600">{c.body}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        onClick={() => handleLike(c.id)}
                        className={`flex items-center gap-1 text-xs transition-colors ${c.liked_by_current_user ? 'text-brand font-medium' : 'text-gray-400 hover:text-brand'}`}
                      >
                        <ThumbsUp className={`w-3 h-3 ${c.liked_by_current_user ? 'fill-current' : ''}`} /> {c.likes_count > 0 && c.likes_count}
                      </button>
                      {auth.user && (
                        <button
                          onClick={() => setReplyingTo(replyingTo?.id === c.id ? null : { id: c.id, name: c.user.name })}
                          className="text-xs text-gray-400 hover:text-brand transition-colors"
                        >
                          Reply
                        </button>
                      )}
                    </div>
                    {c.replies.map(r => (
                      <div key={r.id} className="ml-4 mt-2">
                        <p className="font-semibold text-gray-700 text-xs mb-0.5">{r.user.name}</p>
                        <p className="text-gray-500 text-xs">{r.body}</p>
                      </div>
                    ))}
                    {replyingTo?.id === c.id && (
                      <form onSubmit={handleReply} className="ml-4 mt-2">
                        <textarea
                          value={replyBody}
                          onChange={e => setReplyBody(e.target.value)}
                          placeholder={`Reply to ${replyingTo.name}...`}
                          rows={2}
                          className="w-full text-xs border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-brand/30 resize-none mb-1.5"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button type="submit" className="text-xs text-brand hover:underline font-medium">Post reply</button>
                          <button type="button" onClick={() => setReplyingTo(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      </form>
                    )}
                  </div>
                ))}
              </div>
              {auth.user ? (
                <form onSubmit={handleComment}>
                  <textarea
                    value={commentBody}
                    onChange={e => setCommentBody(e.target.value)}
                    placeholder="Leave a comment..."
                    rows={3}
                    className="w-full text-sm border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-brand/30 resize-none mb-2"
                  />
                  <Button className="w-full">Add comment</Button>
                </form>
              ) : (
                <Link href="/users/sign_in">
                  <Button className="w-full">Sign in to comment</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
